// import necessary modules
import React, { useContext, useState, useEffect } from 'react';
import { MsalContext } from '@azure/msal-react';
import RegistrationLoader from '../Components/Shared/Loader/RegistrationLoader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { completeRegistration, getUserAndItsOrganizations } from '../API/Organization/organization';
import { sendUserAccountVerificationEmail } from "../API/EmailApi";
import { checkIfUserHasStoredApps, createAppFromStoredApp } from '../API/Integrations/Integrations';
import { createContactInHubspot } from '../API/Hubspot/hubspot';
import ShuffleMessage from '../Components/Shared/ShuffleMessage/ShuffleMessage';
import { messagesWhenCreatingProject } from '../Components/Shared/ShuffleMessage/MessagesHelper';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import { AddUserToApplicationBySignupId } from '../API/Runtime/RuntimeHelper';
import { getAccessToken, redirectToRuntimeAppUrl, redirectToWorkspace, selectOrganization } from './authUtils';
import Loader from '../Components/Shared/Loader/loader';
import { callApiWithAuthorization } from '../API/CallApi';
import { getProjectPublishStatus } from '../Components/Dashboard/Project/AliceGPT/utils';

export const AuthMiddleware = () => {

	const navigate = useNavigate();

	const context = useContext(MsalContext) // get msal context
	const accounts = context.accounts || []; // get accounts
	// Detect the primary logged in user using signIn TFP or fallback to the first account.
	let loggedInUser = accounts[0] || null;
	let signupIdentity = loggedInUser?.idTokenClaims.signupIdentity || null;
	let orgId = loggedInUser?.idTokenClaims.orgId || null;
	let roleId = loggedInUser?.idTokenClaims.workspaceRole || null;
	let appUrl = loggedInUser?.idTokenClaims.appUrl || null;
	let coCommand = loggedInUser?.idTokenClaims.co_command || null;

	const [loading, setLoading] = useState(true);
	const [queryParameters] = useSearchParams();
	const [showSubscriptionLimitReached, setShowSubscriptionLimitReached] = useState(false);
	const [creatingProject, setCreatingProject] = useState(false);
	const [organization, setOrganization] = useState(null);

	// State used when user belongs to multiple organizations.
	const [showSelectOrganization, setShowSelectOrganization] = useState(false);
	const [selectedOrganization, setSelectedOrganization] = useState(null);
	const [organizationsOptions, setOrganizationsOptions] = useState([]);

	// Set redirect URL from query parameters if available.
	const redirectUrl = queryParameters.get("redirectUrl");
	if (redirectUrl) {
		localStorage.setItem("redirectUrl", redirectUrl);
	}

	// useEffect: fetch user organizations and handle user/app creation.
	useEffect(() => {
		const checkUserOrganizations = async () => {
			try {
				if (!loggedInUser) {
					navigate('/');
					callApiWithAuthorization("/api/v0/log-user-registration-request", {
						method: "POST",
						headers: {
							"Content-Type": "application/json"
						},
						body: JSON.stringify({
							loggedInUserNotFound: true
						})
					})
					return;
				}

				// Ensure valid access token before processing.
				await getAccessToken(context);
				const { user: fetchedUser, organizations: fetchedOrgs } = await getUserAndItsOrganizations(loggedInUser.username);
				const activeOrgs = (fetchedOrgs || []).filter(org => org.isActive);
				const initialOrg = activeOrgs[0] || {};
				const invitedUser = activeOrgs.length > 0;

				const sidePromises = [checkIfUserHasStoredApps(loggedInUser.username)];

				let updatedUser = fetchedUser;
				let updatedOrgs = activeOrgs;
				let currentOrg = initialOrg;
				let appResponse;

				const shouldCompletedRegistration = !fetchedUser || !fetchedUser.isCompletedRegistration;

				if (shouldCompletedRegistration) {
					let fetchedRegistrationDetails = false;
					const registrationRequestId = localStorage.getItem("registrationRequestId");
					if ((!orgId || !roleId) && registrationRequestId) {
						const req = await callApiWithAuthorization("/api/v0/registration-details/" + registrationRequestId, {
							method: "GET",
							headers: {
								"Content-Type": "application/json"
							}
						})
						const response = await req.json();
						orgId = response.signUpOrgId;
						roleId = response.roleId;
						signupIdentity = response.signupIdentity;
						appUrl = response.appUrl;
						coCommand = response.co_command;
						localStorage.removeItem("registrationRequestId");
						fetchedRegistrationDetails = true;
					}
					const response = await completeRegistration(loggedInUser, orgId, roleId);
					updatedOrgs = response.organizations;
					currentOrg = updatedOrgs[0];
					updatedUser = response.user;
					const correlationId = loggedInUser?.idTokenClaims?.correlationId || "";
					callApiWithAuthorization("/api/v0/log-user-registration-request", {
						method: "POST",
						headers: {
							"Content-Type": "application/json"
						},
						body: JSON.stringify({
							loggedInUser,
							orgId,
							roleId,
							signupIdentity,
							correlationId,
							coCommand,
							registrationRequestId,
							fetchedRegistrationDetails
						})
					})
					if (signupIdentity && orgId) {
						await AddUserToApplicationBySignupId(signupIdentity, updatedUser.azureId);
					}
					if (updatedUser.userVerified === false) {
						sidePromises.push(sendUserAccountVerificationEmail(updatedUser._id, loggedInUser, appUrl));
					}
				}

				const [storedAppResponse] = await Promise.all(sidePromises);
				const { hasStoredApp } = storedAppResponse;

				if (hasStoredApp) {
					if (updatedOrgs.length > 1) {
						setShowSelectOrganization(true);
						setOrganizationsOptions(updatedOrgs);
						setSelectedOrganization(updatedOrgs[0]);
					} else {
						setCreatingProject(true);
						appResponse = await createAppFromStoredApp(updatedOrgs[0]._id, updatedOrgs[0].orgName, loggedInUser.username);
					}
				}

				const selectedOrg = selectOrganization(updatedOrgs, updatedUser);
				setOrganization(selectedOrg);
				localStorage.setItem("selectedOrganization", selectedOrg.orgName);

				if (selectedOrg && coCommand && shouldCompletedRegistration) {
					setCreatingProject(true);
					const req = await callApiWithAuthorization("/api/v0/project/create-project-from-description", {
						method: "POST",
						headers: {
							"Content-Type": "application/json"
						},
						body: JSON.stringify({
							organizationId: selectedOrg._id,
							organizationName: selectedOrg.orgName,
							email: loggedInUser.username,
							description: coCommand
						})
					})
					appResponse = await req.json();
					
					// If we receive a publishStatusId, implement polling mechanism
					if (appResponse.publishStatusId) {
						let response = {};
						while (response.status !== "PUBLISH_SUCCESS" && response.status !== "PUBLISH_FAILED") {
							response = await getProjectPublishStatus(appResponse.publishStatusId);
							if (response.status !== "PUBLISH_SUCCESS" && response.status !== "PUBLISH_FAILED") {
								await new Promise(resolve => setTimeout(resolve, 30000));
							}
						}
						if (response.status === "PUBLISH_SUCCESS") {
							response = response.publishResponse;
						}
						if (response.status === "PUBLISH_FAILED") {
							if (response.message === "Max apps allowed limit reached") {
								setOrganization(selectedOrg);
								setShowSubscriptionLimitReached(true);
								setCreatingProject(false);
								return;
							} else {
								throw new Error(response.message);
							}
						}
						appResponse = response;
					} else if (appResponse.publishStatus === "Success") {
						appResponse.data = appResponse.publishData;
					}
				}

				if (appResponse) {
					if (appResponse.data?.appUrl) {
						window.open(appResponse.data.appUrl + "/DefaultPortal", "_self");
					} else if (appResponse.status === "Failure" && appResponse.message === "Max apps allowed limit reached") {
						setOrganization(selectedOrg);
						setShowSubscriptionLimitReached(true);
					}
					setCreatingProject(false);
				} else if (!hasStoredApp) {
					if (appUrl && signupIdentity) {
						redirectToRuntimeAppUrl(appUrl);
					} else {
						redirectToWorkspace(null);
					}
				}
			} catch (error) {
				// Log error if fetching organizations or creating user/app fails.
				console.error(error);
			} finally {
				setLoading(false);
			}
		};

		if (context.inProgress === "none") {
			debugger;
			const rand = accounts;
			checkUserOrganizations();
		}
	}, [context.inProgress]);

	// function to create app from stored app if applicable.
	const createAppFromStoredAppIfApplicable = async () => {
		const { hasStoredApp } = await checkIfUserHasStoredApps(loggedInUser.username)
		let appResponse
		if (hasStoredApp) {
			setCreatingProject(true);
			appResponse = await createAppFromStoredApp(selectedOrganization._id, selectedOrganization.orgName, loggedInUser.username)
			if (appResponse) {
				// Open the new app URL if created successfully.
				if (appResponse.data && appResponse.data.appUrl) {
					window.open(appResponse.data.appUrl + "/DefaultPortal", "_self");
				} else if (appResponse.status && appResponse.status === "Failure" && appResponse.message === "Max apps allowed limit reached") {
					// Handle subscription limit reached.
					setOrganization(selectedOrganization);
					setShowSubscriptionLimitReached(true);
					setCreatingProject(false);
				}
				setShowSelectOrganization(false);
			}
		}
		return appResponse
	}

	// Function used for redirecting to the workspace.
	function goToWorkspace() {
		window.open(process.env.REACT_APP_RUNTIME_URL + '/' + organization.orgUrl, "_self");
	}
	if (context.inProgress !== "none") return <Loader />;

	// Render loading state with appropriate loader.
	if (creatingProject) {
		return (
			<div className="d-flex flex-column justify-content-center align-items-center w-100 h-100">
				<div style={{ height: "80%" }}>
					<img
						alt="loader-clone"
						height={"100%"}
						src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/loaders/library.gif`}
					/>
				</div>
				<ShuffleMessage data={messagesWhenCreatingProject} interval={3000} />
			</div>
		);
	}

	// Render the UI when subscription limit is reached.
	if (showSubscriptionLimitReached) {
		return (
			<div className="h-100 d-flex justify-content-center align-items-center">
				<div className="jumbotron text-center">
					<img
						className="my-4"
						height={300}
						src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/upgrade.png`}
						alt="Codelessone"
					/>
					<h3>
						You have reached the limit of your Codeless ONE Account.
						<br />
						You can delete your existing apps or contact Sales to upgrade: info@codelessone.com.
					</h3>
					<button className="btn btn-primary mt-4 font-size-4" onClick={goToWorkspace}>
						Go to Homepage
					</button>
				</div>
			</div>
		);
	}

	// Render the organization selection UI if user belongs to multiple organizations.
	if (showSelectOrganization) {
		return (
			<div className="h-100 d-flex justify-content-center align-items-center">
				<div className="jumbotron text-center">
					<h3>It looks like you're part of multiple organizations!</h3>
					<div>
						<p className="font-size-2">Please select an organization to continue.</p>
						<DropDownList
							data={organizationsOptions}
							textField="orgName"
							dataItemKey="_id"
							value={selectedOrganization}
							onChange={(e) => setSelectedOrganization(e.target.value)}
							style={{ width: "100%" }}
						/>
					</div>
					<br />
					<Button themeColor="primary" onClick={createAppFromStoredAppIfApplicable}>
						Create App
					</Button>
				</div>
			</div>
		);
	}

	// Render appropriate loader based on whether the user is new.
	const newUser = loggedInUser?.idTokenClaims.newUser || false;
	if (loading) {
		return newUser ? (
			<RegistrationLoader heading="Welcome to Codeless ONE!" subheading="We are getting everything ready for you." />
		) : (
			<Loader />
		);
	}
};
